import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { Skeleton } from '../../components/ui/Skeleton'
import { useToast } from '../../components/ui/ToastProvider'
import { usePollWhilePending, useRefetchOnWindowFocus } from '../../lib/useRefetchOnWindowFocus'
import { NewWorkspaceModal } from '../workspaces/components/NewWorkspaceModal'
import type { WorkspaceSummary } from '../workspaces/types'
import { defaultWorkspacesService, useWorkspaceList } from '../workspaces/useWorkspaces'
import type { WorkspacesService } from '../workspaces/useWorkspaces'
import { useUploadFlowContext } from './UploadFlowProvider'
import { ConfirmedSessionRow } from './components/ConfirmedSessionRow'
import { Dropzone, UPLOADS_HELPER_TEXT } from './components/Dropzone'
import { InProgressSessionCard } from './components/InProgressSessionCard'
import { WorkspaceSelector } from './components/WorkspaceSelector'
import { defaultUploadsService, useUploadSessions } from './useUploads'
import type { UploadsService } from './useUploads'

export interface UploadsPageProps {
  /** Injectable for tests / future BFF-backed swap. */
  readonly service?: UploadsService
  /** Injectable for tests — same DI seam pattern as `service`. */
  readonly workspacesService?: WorkspacesService
}

const SESSION_SKELETON_KEYS = ['a', 'b', 'c']

/**
 * UID-589 (Task 9) — how recently a session must have been created before its
 * missing thumbnails are worth POLLING for.
 *
 * WHY THIS GATE EXISTS. `stillGeneratingThumbnails` below asks "is any
 * in-progress session short of thumbnails", and on its own that condition can
 * NEVER GO FALSE for an abandoned session. A session whose upload was
 * interrupted — tab closed, browser crash, a render that threw — keeps
 * `status: 'uploading'` forever: the wire status enum is
 * `[uploading, identifying, ready]` (`openapi/schemas/intake.yaml`), nothing
 * transitions it on abandonment, and there is no reaper for
 * `intake.staging_session`. `listSessions` has no status filter and no LIMIT,
 * so every such session is returned on every read, forever.
 *
 * The net effect, reported from demo: merely VISITING /uploads with one stale
 * session present fired ~10 `GET /uploads/sessions` requests over 20 s — every
 * visit, indefinitely — polling for a render that had already failed or was
 * never going to be attempted in this browser at all.
 *
 * WHY AN AGE BOUND IS THE RIGHT SHAPE. Thumbnails are rendered CLIENT-SIDE by
 * the uploading browser and PUT to S3 moments after the upload (UID-330), so
 * for any session older than a few minutes the poll is provably futile — and
 * for a session uploaded from ANOTHER device it is futile from the first tick,
 * because this browser will never render those PNGs.
 *
 * This narrows only the POLLING TRIGGER. Both rendered lists below still show
 * every session exactly as before — the fix must not, and does not, change what
 * is on screen.
 *
 * It cannot cut a legitimate poll short either: `usePollWhilePending`'s own
 * ceiling is 20 s from when polling starts, which for a freshly-created session
 * always elapses long before this 3-minute bound is reached.
 */
const THUMBNAIL_POLL_MAX_SESSION_AGE_MS = 3 * 60_000

/** Exported for the test that pins the window; see the constant's doc comment. */
export function isWithinThumbnailPollWindow(createdAt: string, now: number = Date.now()): boolean {
  const created = new Date(createdAt).getTime()
  // Fail OPEN: an unparseable timestamp keeps the pre-Task-9 behaviour.
  if (Number.isNaN(created)) {
    return true
  }
  return now - created < THUMBNAIL_POLL_MAX_SESSION_AGE_MS
}

/**
 * Uploads (UID-41/UID-140, `Fictiv Platform.dc.html:2416-2507`) — landing
 * page for starting a new CAD upload batch, assigning it to a workspace,
 * and picking up "in progress" and "recently confirmed" sessions. See
 * `useUploads.ts`'s doc comment for exactly which parts of this screen are
 * real BFF calls vs. the typed-but-TODO seam (`confirmSession`/
 * `deleteSession`/`seedSampleSession`) pending UID-141/UID-146.
 *
 * The presign→PUT→create-session→navigate flow itself lives in
 * `useUploadFlow` (shared with Home) — see that hook's doc comment.
 */
export function UploadsPage({
  service = defaultUploadsService,
  workspacesService = defaultWorkspacesService,
}: UploadsPageProps = {}) {
  const { state, refetch } = useUploadSessions(service)
  // UID-335 — a thumbnail generated while this tab was in the background
  // (UID-330's async client-side render) shows up without a manual reload.
  // Scoped to THIS page's `refetch`, not inside `useUploadSessions` itself —
  // that hook is also called from the globally-mounted `Sidebar`, where a
  // refetch-on-focus would fire a network call on every tab-switch on every
  // authenticated page, not just this one.
  useRefetchOnWindowFocus(refetch)
  // UID-335 follow-up — the companion case `useRefetchOnWindowFocus` alone
  // misses: the user who uploads and stays on THIS tab the whole time, never
  // triggering a visibility transition. This used to be a single fixed-delay
  // check, which was a guess about generation time that a heavier render
  // pipeline (shadow maps, PMREM bake, occt WASM) can now miss — a session
  // still short on thumbnails just gets re-checked every 2s, and the poll
  // stops itself the moment every in-progress session has caught up (or
  // after 20s, so a render failure can't poll forever).
  const stillGeneratingThumbnails =
    state.status === 'ready' &&
    state.sessions.some(
      (session) =>
        (session.status === 'uploading' || session.status === 'identifying') &&
        (session.thumbnailUrls?.length ?? 0) < session.fileCount &&
        // UID-589 Task 9 — see THUMBNAIL_POLL_MAX_SESSION_AGE_MS. Deliberately
        // FAIL-OPEN on an unparseable/absent `createdAt` (`Number.isNaN` →
        // treated as recent), so anything unexpected in the timestamp keeps
        // today's polling behaviour rather than silently suppressing a poll a
        // real upload needed.
        isWithinThumbnailPollWindow(session.createdAt),
    )
  usePollWhilePending(refetch, stillGeneratingThumbnails, 2000, 20000)
  const { state: workspaceState } = useWorkspaceList(workspacesService)
  const navigate = useNavigate()
  const location = useLocation()
  const { showToast } = useToast()
  // UID-583 (ADR-0173 D2) — the upload flow is owned by `UploadFlowProvider`
  // above `<Routes>`, not by this page. It has to be: this page unmounts as
  // soon as the session exists (D1 navigates there), while the PUTs, the
  // thumbnail renders and the BOM-hierarchy capture all still have to
  // finish. The `onNotice`→`showToast` wiring that used to be here moved
  // there with it.
  const { batchError, handleFilesSelected, clearBatchError } = useUploadFlowContext()
  // A batch error belongs to the selection that produced it. The provider
  // outlives this page, so without this a failed batch's message would still
  // be on screen on the next visit — see `clearBatchError`'s own doc.
  useEffect(() => clearBatchError(), [clearBatchError])

  /**
   * UID-433 gap #2 — `WorkspaceUploadMenu`'s "Upload files or CAD" navigates
   * here with `state: { workspaceId }` from a project's own Parts tab; that
   * project should win over the "first joined" default below. CAPTURED ONCE
   * via a lazy initializer (same pattern as `CartPage`'s
   * `openGenerateQuoteModal` handoff, ADR-0125 D8) so it survives the
   * consume step's re-render — but NOT trusted directly (code-review W3):
   * the defaulting effect below validates it against the LOADED list (same
   * `myRole !== null` membership check the "first joined" default already
   * applies) before selecting it, so an unknown/non-member id can't leave
   * `selectedWorkspaceId` holding a value `WorkspaceSelector` can't resolve
   * to a name (its own fallback, `workspace not found → 'Keep as draft'`,
   * would then silently disagree with what's actually in local state).
   */
  const [handoffWorkspaceId] = useState<string | null>(() => {
    const handoff = location.state as { workspaceId?: string } | null
    return handoff?.workspaceId ?? null
  })
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null)
  const [workspaceDefaulted, setWorkspaceDefaulted] = useState(false)
  const [newWorkspaceOpen, setNewWorkspaceOpen] = useState(false)
  // UID-354 — projects created from THIS page, held locally and shown ahead of
  // the fetched list. See the `onCreate` handler for why this isn't a refetch.
  const [createdWorkspaces, setCreatedWorkspaces] = useState<readonly WorkspaceSummary[]>([])
  const [sampleError, setSampleError] = useState<string | null>(null)
  const [sampleLoading, setSampleLoading] = useState(false)

  // Consume the handoff payload — `location.state` belongs to the HISTORY
  // ENTRY, not this component, so an unconsumed intent would re-preselect on
  // Back/refresh with no way for the buyer to switch projects and have it
  // stick. `replace: true` rewrites the current entry rather than pushing a
  // new one. The already-captured `selectedWorkspaceId`/`workspaceDefaulted`
  // above are untouched by this.
  useEffect(() => {
    const handoff = location.state as { workspaceId?: string } | null
    if (handoff?.workspaceId == null) return
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
  }, [location, navigate])

  // Default-select the project handoff'd from a Parts tab (UID-433 gap #2)
  // if it's actually in the loaded, joined list — else the first workspace
  // the caller has actually joined (myRole owner/member). Only once, the
  // first time the list loads: `workspaceDefaulted` latches so a LATER
  // explicit selection here (or a refetch) is never clobbered back.
  useEffect(() => {
    if (workspaceDefaulted || workspaceState.status !== 'ready') return
    const joined = workspaceState.workspaces.filter((workspace) => workspace.myRole !== null)
    const handoffMatch = joined.find((workspace) => workspace.id === handoffWorkspaceId)
    const defaulted = handoffMatch ?? joined[0]
    if (defaulted) setSelectedWorkspaceId(defaulted.id)
    setWorkspaceDefaulted(true)
  }, [workspaceState, workspaceDefaulted, handoffWorkspaceId])

  const listedWorkspaces = workspaceState.status === 'ready' ? workspaceState.workspaces : []
  // UID-354 — locally-created projects first (comp `:7327` prepends), then the
  // fetched list with any of them filtered out. The dedupe is REQUIRED, and a
  // refetch is not what makes it so: the selector renders unconditionally, so a
  // project can be created while the INITIAL `listWorkspaces()` is still in
  // flight, and that response then already contains it. Without this filter
  // that lands as two rows sharing one `id` — which is also a duplicate React
  // key, since `WorkspaceSelector` keys rows by `workspace.id`.
  //
  // PRECONDITION: created-first means the local copy wins on EVERY field, not
  // just ordering, and `createdWorkspaces` never clears. That is only safe
  // because this page never refetches the workspace list, so there is never
  // fresher server data for the stale copy to shadow. If a refetch is ever
  // added here (e.g. via a shared workspace cache — see the Sidebar staleness
  // note in the UID-349 handoff), the LISTED copy must win on fields while
  // created-first is kept for order:
  //   const listedById = new Map(listedWorkspaces.map((w) => [w.id, w]))
  //   [...createdWorkspaces.map((c) => listedById.get(c.id) ?? c), ...rest]
  //
  // That same replacement also fixes a second, quieter gap: these rows are
  // `Workspace` values widened to `WorkspaceSummary`, so they carry fields the
  // list contract doesn't declare and LACK the optional `partCount`/`bomCount`
  // that `types.ts` documents consumers must branch on. Harmless while only
  // `WorkspaceSelector` reads them (it reads neither count), but any future
  // consumer of this merged list that shows counts needs the listed copy.
  const createdIds = new Set(createdWorkspaces.map((workspace) => workspace.id))
  const workspaces = [
    ...createdWorkspaces,
    ...listedWorkspaces.filter((workspace) => !createdIds.has(workspace.id)),
  ]

  async function handleConfirmSession(sessionId: string) {
    await service.confirmSession(sessionId)
    refetch()
  }

  async function handleDeleteSession(sessionId: string) {
    await service.deleteSession(sessionId)
    refetch()
  }

  async function handleAnalyzeSample() {
    setSampleError(null)
    setSampleLoading(true)
    try {
      const session = await service.seedSampleSession()
      navigate(`/uploads/${session.sessionId}`)
    } catch (error) {
      setSampleError(
        error instanceof Error ? error.message : 'We couldn’t create a sample fixture set.',
      )
    } finally {
      setSampleLoading(false)
    }
  }

  const inProgressSessions =
    state.status === 'ready' ? state.sessions.filter((s) => s.status !== 'confirmed') : []
  const confirmedSessions =
    state.status === 'ready' ? state.sessions.filter((s) => s.status === 'confirmed') : []

  return (
    <section aria-labelledby="uploads-heading" className="uploads-page">
      <div>
        <h1 id="uploads-heading" className="display-heading">
          Uploads
        </h1>
        <p className="uploads-hero-subtitle">
          Drop a batch of files — each upload becomes a session you can return to, resolve, and
          confirm into your library.
        </p>
      </div>

      <div className="card uploads-dropzone-card">
        <Dropzone
          bare
          onFilesSelected={(selected) =>
            handleFilesSelected(selected, selectedWorkspaceId ?? undefined)
          }
          title="Drop CAD files and drawings to start a new upload"
          helperText={UPLOADS_HELPER_TEXT}
        />
        <WorkspaceSelector
          workspaces={workspaces}
          selectedWorkspaceId={selectedWorkspaceId}
          onSelectWorkspace={setSelectedWorkspaceId}
          onSelectDraft={() => setSelectedWorkspaceId(null)}
          onCreateWorkspace={() => setNewWorkspaceOpen(true)}
        />
      </div>

      {batchError && (
        <p role="alert" className="uploads-batch-error">
          {batchError}
        </p>
      )}

      <p className="uploads-sample-link">
        Just exploring?{' '}
        <button
          type="button"
          className="uploads-sample-link-cta"
          onClick={() => void handleAnalyzeSample()}
          disabled={sampleLoading}
        >
          {sampleLoading ? 'Preparing sample…' : 'Analyze a sample fixture set →'}
        </button>
      </p>
      {sampleError && (
        <p role="alert" className="uploads-batch-error">
          {sampleError}
        </p>
      )}

      {state.status === 'loading' && (
        <div aria-busy="true" aria-label="Loading recent uploads" className="uploads-skeleton">
          {SESSION_SKELETON_KEYS.map((key) => (
            <Skeleton key={key} height={64} />
          ))}
        </div>
      )}

      {state.status === 'error' && (
        <EmptyState
          title="We couldn’t load recent uploads"
          detail={state.message}
          action={<Button onClick={refetch}>Try again</Button>}
        />
      )}

      {state.status === 'ready' && state.sessions.length === 0 && (
        <EmptyState title="No uploads yet" detail="Drop CAD files above to get started." />
      )}

      {state.status === 'ready' && inProgressSessions.length > 0 && (
        <section aria-labelledby="uploads-in-progress-heading" className="uploads-section">
          <div className="uploads-section-header">
            <h2 id="uploads-in-progress-heading">In progress</h2>
            <span className="uploads-count-pill">{inProgressSessions.length}</span>
            <span className="caption-text">Pick up where you left off</span>
          </div>
          <div className="uploads-session-list">
            {inProgressSessions.map((session) => (
              <InProgressSessionCard
                key={session.sessionId}
                session={session}
                onConfirm={handleConfirmSession}
                onResume={(sessionId) => navigate(`/uploads/${sessionId}`)}
                onDelete={handleDeleteSession}
              />
            ))}
          </div>
        </section>
      )}

      {state.status === 'ready' && confirmedSessions.length > 0 && (
        <section aria-labelledby="uploads-confirmed-heading" className="uploads-section">
          <div className="uploads-section-header">
            <h2 id="uploads-confirmed-heading">Recently confirmed</h2>
            <span className="caption-text">Already in your library</span>
          </div>
          <div className="card uploads-confirmed-list">
            {confirmedSessions.map((session) => (
              <ConfirmedSessionRow key={session.sessionId} session={session} />
            ))}
          </div>
        </section>
      )}

      {/* UID-353 — create a project WITHOUT leaving Uploads. This replaces a
          `navigate('/projects')`, which threw away the user's in-progress
          upload just to make a container to file it in. Same shared modal
          `AllWorkspacesPage` renders (comp `Fictiv Platform.dc.html:7319`
          reaches one `newProjectModal` from both entry points), deliberately
          NOT a copy specialized for this screen. */}
      <NewWorkspaceModal
        open={newWorkspaceOpen}
        onClose={() => setNewWorkspaceOpen(false)}
        onCreate={async (request) => {
          let created: WorkspaceSummary
          try {
            created = await workspacesService.createWorkspace(request)
          } catch (error) {
            // UID-355 — surface the failure. `NewWorkspaceModal` swallows the
            // rejection by design (its own comment declares that the CALLER
            // toasts), so without this a 403 from `workspace:manage`
            // (account_admin-only — see UID-356) or a network drop left the
            // user with a re-enabled button and no explanation at all.
            showToast(
              error instanceof Error ? error.message : 'Could not create the project. Try again.',
              'error',
            )
            // Rethrow so `NewWorkspaceModal.handleSubmit`'s catch still sees a
            // failure and keeps the modal open for a retry. NOTE: the modal
            // already calls `onClose()` INSIDE its `try`, after the await, so a
            // rejection would leave it open regardless — the rethrow matters
            // because THIS handler caught the error to toast it, and swallowing
            // it here is what would make the modal close as if it had worked.
            throw error
          }
          // UID-354 (AC2) — prepend locally and select, mirroring the comp's
          // state mapper (`Fictiv Platform.dc.html:7327`:
          // `userProjects:[proj,...st.userProjects]`, `uploadProject:id`).
          //
          // Deliberately NOT a list `refetch()`, which is what
          // `AllWorkspacesPage.tsx:138` does: `useWorkspaceList` sets
          // `{status:'loading'}` first (`useWorkspaces.ts:185`), collapsing
          // `listedWorkspaces` to `[]` for one round trip. During that window
          // `WorkspaceSelector`'s `.find()` misses and the trigger label falls
          // back to "Keep as draft (unfiled)" — the exact opposite of the
          // truth immediately after a successful create. `AllWorkspacesPage`
          // gets away with it only because it navigates away.
          //
          // Dedupe against the fetched list happens where the two are merged
          // above — a created project CAN also arrive in the initial
          // `listWorkspaces()` response, so this list is not automatically
          // disjoint from it.
          setCreatedWorkspaces((current) => [created, ...current])
          setSelectedWorkspaceId(created.id)
          // Comp `:7328` — `flash('Project “'+name+'” created')`.
          showToast(`Project “${created.name}” created`, 'success')
          // Creating a project is an explicit choice, so pre-empt the
          // default-select effect above: without this, a create completing
          // while the list is still in flight would be silently overwritten by
          // the "first joined workspace" default when the list resolves.
          setWorkspaceDefaulted(true)
        }}
      />
    </section>
  )
}
